import { BadgeCheck, Clock3, ShieldCheck } from "lucide-react";
import type { MarketplaceSetupView } from "../../lib/api";

export type AreaChip = {
  id: string;
  label: string;
  zips: string[];
};

/** A single-ZIP chip — the shape used everywhere a lone ZIP becomes a chip. */
export function zipChip(zip: string): AreaChip {
  return { id: zip, label: zip, zips: [zip] };
}

// The top-right delivery badge. `delivery_state` collapses THREE distinct
// backend gaps into "none" (marketplace/supply/gate.go DeliveryState: inactive
// availability, no card on file, OR missing/stale verification), so a bare
// "none" must NOT assert "Verification not submitted" — a submitted-but-
// cardless agent would be told, falsely, that their verification is missing.
// When delivery isn't live, fall back to the ACTUAL verification status so the
// badge never contradicts the Profile page's verification card.
export function deliveryBadge(setup: MarketplaceSetupView) {
  if (setup.delivery_state === "verified") {
    return {
      icon: BadgeCheck,
      label: "Verified delivery",
      className: "border-emerald-200 bg-emerald-50 text-emerald-800",
    };
  }
  if (setup.delivery_state === "pending_labeled") {
    return {
      icon: Clock3,
      label: "Pending, labeled delivery",
      className: "border-amber-200 bg-amber-50 text-amber-800",
    };
  }
  const status = setup.verification?.status ?? "not_submitted";
  if (status === "banned" || status === "revoked") {
    return {
      icon: ShieldCheck,
      label: "Marketplace access paused",
      className: "border-amber-200 bg-amber-50 text-amber-800",
    };
  }
  if (status === "submitted" || status === "under_review") {
    return {
      icon: Clock3,
      label: "Verification in review",
      className: "border-amber-200 bg-amber-50 text-amber-800",
    };
  }
  if (status === "verified") {
    return {
      icon: ShieldCheck,
      label: "Finish delivery setup",
      className: "border-border bg-card text-fg-muted",
    };
  }
  return {
    icon: ShieldCheck,
    label: "Verification not submitted",
    className: "border-border bg-card text-fg-muted",
  };
}

// The availability status pill (SILL-512) — a dot-chip mirror of
// `RelationshipStageChip`'s idiom (tinted pill, leading `bg-current`-style
// dot) placed beside `deliveryBadge` in the header. This is DELIBERATELY a
// separate signal from `deliveryBadge`: availability is the agent's own
// on/off/discoverable dial, not a derived delivery-readiness state, and the
// two must never be collapsed into one chip. Unlike `deliveryBadge` (which
// throws no unknown-state case because `MarketplaceDeliveryState` is fully
// enumerated in the switch above), `setup.availability.state` is
// intentionally treated as an open string at runtime — a future/legacy wire
// value must fail CLOSED to the same "Off" chip rather than crash or imply a
// visibility state that isn't real (SILL-512 invariant: never a false
// "Active"/"Discoverable").
export function availabilityPill(setup: MarketplaceSetupView) {
  const state: string = setup.availability?.state ?? "off";
  if (state === "active") {
    return {
      label: "Active",
      className: "border-emerald-200 bg-emerald-50 text-emerald-800",
      dotClassName: "bg-emerald-500",
    };
  }
  if (state === "offline_discoverable") {
    return {
      label: "Discoverable",
      className: "border-blue-200 bg-blue-50 text-blue-800",
      dotClassName: "bg-blue-500",
    };
  }
  // "off" and any unrecognized state fail closed to the same neutral chip.
  return {
    label: "Off",
    className: "border-border bg-card text-fg-muted",
    dotClassName: "bg-fg-muted",
  };
}

/**
 * Resolves a raw 5-digit ZIP string to an `AreaChip`.
 *
 * The hardcoded `AREA_LOOKUP` neighborhood table has been removed (D8: neighborhoods
 * resolve via the backend typeahead, not a client-side list). This function now
 * handles only raw 5-digit ZIPs entered directly into the input; all other
 * queries go through the `onTypeahead` prop and the backend gazetteer.
 */
export function resolveServiceAreaToken(raw: string): AreaChip | null {
  const cleaned = raw.trim();
  if (!cleaned) return null;
  if (/^\d{5}$/.test(cleaned)) {
    return zipChip(cleaned);
  }
  return null;
}

/**
 * Display text for the saved "licensed since" year: the year plus the derived
 * years-licensed ("2014 · 12 years"). Years are currentYear − year, the same
 * rule the backend resolver uses; a zero-year gap shows the bare year, never
 * "0 years".
 */
export function licensedSinceLabel(year: number, currentYear: number): string {
  const years = currentYear - year;
  if (years <= 0) return String(year);
  return `${year} · ${years} ${years === 1 ? "year" : "years"}`;
}
